import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCompany } from "@/context/CompanyContext";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Banknote, ArrowDownToLine, ArrowUpFromLine, CheckCircle2,
  CircleDollarSign, RotateCcw, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/formatDate";

function fmtChf(v, currency = "CHF") {
  if (v == null || v === "") return "—";
  return new Intl.NumberFormat("de-CH", { style: "currency", currency }).format(Number(v));
}

const STATUS_BADGE = {
  active: { text: "Aktiv", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  repaid: { text: "Zurückgezahlt", className: "bg-slate-50 text-slate-700 border-slate-200" },
  cancelled: { text: "Storniert", className: "bg-rose-50 text-rose-700 border-rose-200" },
};

const ROW_STATUS_BADGE = {
  planned: { text: "Geplant", className: "bg-slate-50 text-slate-600 border-slate-200" },
  booked: { text: "Gebucht", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function LoanDetail() {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const { company } = useCompany();
  const companyId = company?._id;
  const [loan, setLoan] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!companyId || !loanId) return;
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        apiClient.get(`/companies/${companyId}/loans/${loanId}`),
        apiClient.get(`/companies/${companyId}/loans/${loanId}/schedule`),
      ]);
      setLoan(l.data);
      setSchedule(s.data);
    } catch (e) {
      toast.error("Darlehen konnte nicht geladen werden");
      navigate("/loans");
    } finally {
      setLoading(false);
    }
  }, [companyId, loanId, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function handlePostDisbursement() {
    if (!confirm("Auszahlung jetzt im Journal buchen? Dies erstellt eine Buchung Bank ↔ Darlehen.")) return;
    setPosting(true);
    try {
      await apiClient.post(`/companies/${companyId}/loans/${loanId}/post-disbursement`);
      toast.success("Auszahlung gebucht");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
    } finally {
      setPosting(false);
    }
  }

  async function handleBookInstallment(period) {
    if (!confirm(`Rate Nr. ${period} jetzt buchen?`)) return;
    try {
      await apiClient.post(
        `/companies/${companyId}/loans/${loanId}/installments/book`,
        { period },
      );
      toast.success(`Rate ${period} gebucht`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
    }
  }

  async function handleCancel() {
    if (!confirm("Darlehen wirklich stornieren? (Status → cancelled)")) return;
    try {
      await apiClient.put(`/companies/${companyId}/loans/${loanId}`, { status: "cancelled" });
      toast.success("Darlehen storniert");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  }

  if (loading || !loan) {
    return <div className="p-6 text-slate-500">Lade ...</div>;
  }

  const isIncoming = loan.direction === "incoming";
  const DirIcon = isIncoming ? ArrowDownToLine : ArrowUpFromLine;
  const statusCfg = STATUS_BADGE[loan.status] || STATUS_BADGE.active;
  const isDisbursed = !!loan.disbursement_journal_entry_id;
  const isLoanActive = loan.status === "active";

  return (
    <div className="p-6 space-y-6" data-testid="loan-detail-page">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/loans")} className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zur Liste
          </Button>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Banknote className="h-7 w-7 text-emerald-600" />
            {loan.counterparty_name}
            <Badge variant="outline" className={statusCfg.className}>{statusCfg.text}</Badge>
          </h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
            <DirIcon className="h-4 w-4" />
            {isIncoming ? "Wir erhalten dieses Darlehen" : "Wir gewähren dieses Darlehen"}
          </div>
        </div>
        <div className="flex gap-2">
          {!isDisbursed && isLoanActive && (
            <Button onClick={handlePostDisbursement} disabled={posting} data-testid="loan-post-disbursement-btn">
              <CircleDollarSign className="h-4 w-4 mr-2" />
              Auszahlung buchen
            </Button>
          )}
          {isLoanActive && (
            <Button variant="outline" onClick={handleCancel} data-testid="loan-cancel-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Stornieren
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase">Darlehenssumme</div>
          <div className="text-2xl font-bold font-mono mt-1">{fmtChf(loan.principal, loan.currency)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase">Zinssatz p.a.</div>
          <div className="text-2xl font-bold mt-1">{loan.interest_rate_pct?.toFixed?.(2) || "0.00"}%</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase">Bereits getilgt</div>
          <div className="text-2xl font-bold font-mono mt-1 text-emerald-600">
            {fmtChf(schedule?.total_paid || 0, loan.currency)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase">Noch offen</div>
          <div className="text-2xl font-bold font-mono mt-1 text-amber-600">
            {fmtChf(schedule?.total_open || 0, loan.currency)}
          </div>
        </Card>
      </div>

      {/* Stammdaten */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">Vertragsdaten</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <div><div className="text-xs text-slate-500">Start</div><div>{formatDate(loan.start_date)}</div></div>
          <div><div className="text-xs text-slate-500">Ende</div><div>{loan.end_date ? formatDate(loan.end_date) : "—"}</div></div>
          <div><div className="text-xs text-slate-500">Laufzeit</div><div>{loan.term_months ? `${loan.term_months} Mt.` : "—"}</div></div>
          <div><div className="text-xs text-slate-500">Frequenz</div><div>{loan.repayment_frequency}</div></div>
          <div><div className="text-xs text-slate-500">Tilgungsart</div><div>{loan.repayment_type}</div></div>
          <div><div className="text-xs text-slate-500">IBAN</div><div className="font-mono">{loan.iban || "—"}</div></div>
          <div><div className="text-xs text-slate-500">Auszahlung gebucht</div>
            <div>{isDisbursed
              ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />Ja</Badge>
              : <Badge variant="outline" className="bg-amber-50 text-amber-700">Ausstehend</Badge>}
            </div>
          </div>
          <div><div className="text-xs text-slate-500">Notiz</div><div className="text-slate-700">{loan.notes || "—"}</div></div>
        </div>
      </Card>

      {/* Tilgungsplan */}
      <Card className="overflow-hidden" data-testid="loan-schedule-card">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Tilgungsplan ({schedule?.total_periods || 0} Raten)</h3>
          {!isDisbursed && (
            <div className="text-xs text-amber-600 flex items-center gap-1">
              💡 Buche zuerst die Auszahlung, bevor du einzelne Raten erfasst.
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-right">#</th>
                <th className="px-3 py-2">Fällig am</th>
                <th className="px-3 py-2 text-right">Kapital</th>
                <th className="px-3 py-2 text-right">Zinsen</th>
                <th className="px-3 py-2 text-right">Gesamt</th>
                <th className="px-3 py-2 text-right">Restschuld</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {schedule?.rows?.map((row) => {
                const cfg = ROW_STATUS_BADGE[row.status] || ROW_STATUS_BADGE.planned;
                return (
                  <tr key={row.period} className="border-b hover:bg-slate-50" data-testid={`loan-installment-row-${row.period}`}>
                    <td className="px-3 py-2 text-right font-mono">{row.period}</td>
                    <td className="px-3 py-2">{formatDate(row.due_date)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtChf(row.principal_amount, loan.currency)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtChf(row.interest_amount, loan.currency)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtChf(row.total_amount, loan.currency)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtChf(row.remaining_balance, loan.currency)}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className={cfg.className}>{cfg.text}</Badge></td>
                    <td className="px-3 py-2 text-right">
                      {row.status !== "booked" && isDisbursed && isLoanActive && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBookInstallment(row.period)}
                          data-testid={`loan-book-installment-${row.period}`}
                        >
                          Buchen
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
